import React, { useState } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Edit, Trash2, Plus, Monitor, FileText } from "lucide-react";
import { Label } from "./ui/label";
import { System } from "../types/index";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export default function SystemSettings() {
  const { systems, addSystem, updateSystem, deleteSystem, procedures, activities, departments, users } = useData();
  const { currentUser } = useAuth();
  const [openNewSystem, setOpenNewSystem] = useState(false);
  const [openEditSystem, setOpenEditSystem] = useState(false);
  const [openDeleteSystem, setOpenDeleteSystem] = useState(false);
  const [openUsageReport, setOpenUsageReport] = useState(false);
  const [selectedSystemForReport, setSelectedSystemForReport] = useState<System | null>(null);
  const [newSystemName, setNewSystemName] = useState("");
  const [editingSystem, setEditingSystem] = useState<System | null>(null);
  const [deleteSystemId, setDeleteSystemId] = useState("");

  const isAdmin = currentUser?.userRole === "Admin";

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>System Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p>You don't have permission to access system settings.</p>
        </CardContent>
      </Card>
    );
  }

  const handleAddSystem = () => {
    if (!newSystemName) {
      toast.error("System name is required");
      return;
    }

    // Check if the system already exists
    if (systems.some(s => s.name.toLowerCase() === newSystemName.toLowerCase())) {
      toast.error("A system with this name already exists");
      return;
    }

    addSystem({
      name: newSystemName,
      active: true
    });

    setNewSystemName("");
    setOpenNewSystem(false);
  };

  const handleUpdateSystem = () => {
    if (!editingSystem || !editingSystem.name || !editingSystem.id) {
      toast.error("System name is required");
      return;
    }

    // Check if the system name already exists (excluding the current system)
    if (
      systems.some(
        s => s.id !== editingSystem.id && s.name.toLowerCase() === editingSystem.name.toLowerCase()
      )
    ) {
      toast.error("A system with this name already exists");
      return;
    }

    // If trying to deactivate the system, check if it's in use
    if (editingSystem.active === false) {
      const systemName = editingSystem.name;
      
      // Check procedures
      const usedInProcedures = procedures.filter(
        proc => {
          const procSystems = (proc?.systems ?? [] as System[]).map(sys => typeof sys !== 'string' ? sys.name : "");
          return procSystems.includes(systemName)
        }
      );
      
      // Check activities
      const usedInActivities = activities.filter(
        act => act.systemsUsed?.includes(systemName)
      );

      if (usedInProcedures.length > 0 || usedInActivities.length > 0) {
        toast.error(
          `Cannot deactivate system "${systemName}" as it is currently in use by ${usedInProcedures.length} procedure(s) and ${usedInActivities.length} activity(ies). Please remove it from all procedures and activities first.`
        );
        return;
      }
    }

    updateSystem(editingSystem.id, {
      name: editingSystem.name,
      active: editingSystem.active
    });

    setEditingSystem(null);
    setOpenEditSystem(false);
  };

  const handleDeleteSystem = () => {
    if (!deleteSystemId) {
      toast.error("No system selected for deletion");
      return;
    }
    deleteSystem(deleteSystemId);
    setDeleteSystemId("");
    setOpenDeleteSystem(false);
  };

  const toggleSystemActive = (system: System) => {
    if (!system.id) {
      toast.error("Cannot update system status");
      return;
    }
    updateSystem(system.id, { active: !system.active });
  };

  const getSystemUsage = (systemName: string) => {
    const usedInProcedures = procedures.filter(proc => {
      const procSystems = (proc?.systems ?? [] as System[]).map(sys => typeof sys !== 'string' ? sys.name : "");
      return procSystems.includes(systemName);
    });
    const usedInActivities = activities.filter(act => act.systemsUsed?.includes(systemName));
    return { procedures: usedInProcedures, activities: usedInActivities };
  };

  const downloadUsageReport = (systemName: string) => {
    const usage = getSystemUsage(systemName);
    
    // Prepare CSV content
    const csvContent = [
      'Type,Name,Department,Owner/Responsible',
      // Add procedures
      ...usage.procedures.map(proc => {
        const department = departments?.find(d => d.departmentCode === proc.departmentId)?.departmentName || 'Unknown';
        const owner = users?.find(u => u.id === proc.procedureOwner)?.name || 'Unknown';
        return `Procedure,"${proc.procedureName}","${department}","${owner}"`;
      }),
      // Add activities
      ...usage.activities.map(act => {
        const procedure = procedures.find(p => p.id === act.procedure);
        const department = departments?.find(d => d.departmentCode === procedure?.departmentId)?.departmentName || 'Unknown';
        const responsible = users?.find(u => u.id === act.responsiblePerson)?.name || 'Unknown';
        return `Activity,"${act.activityName}","${department}","${responsible}"`;
      })
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${systemName}_usage_report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>System Settings</CardTitle>
        <Dialog open={openNewSystem} onOpenChange={setOpenNewSystem}>
          <DialogTrigger asChild>
            <Button>
              <Plus size={16} className="mr-2" />
              Add System
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New System</DialogTitle>
              <DialogDescription>
                Add a new system that can be used in activities.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="systemName">System Name</Label>
                <Input
                  id="systemName"
                  value={newSystemName}
                  onChange={(e) => setNewSystemName(e.target.value)}
                  placeholder="Enter system name"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleAddSystem}>Add System</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Active</TableHead>
                <TableHead>System Name</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {systems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">
                    No systems found
                  </TableCell>
                </TableRow>
              ) : (
                systems
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((system) => (
                    <TableRow key={system.id} className="h-16">
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${system.active ? "bg-green-500" : "bg-gray-300"}`} />
                          <span>{system.active ? "Active" : "Inactive"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <Monitor size={16} className="text-muted-foreground shrink-0" />
                          <span>{system.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-4">
                        <div className="flex justify-end gap-2">
                          <Dialog
                            open={openUsageReport && selectedSystemForReport?.id === system.id}
                            onOpenChange={(open) => {
                              setOpenUsageReport(open);
                              if (open) setSelectedSystemForReport(system);
                              else setSelectedSystemForReport(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button variant="outline" size="icon">
                                <FileText size={16} />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>System Usage Report - {system.name}</DialogTitle>
                                <DialogDescription>
                                  View where this system is being used and download the report.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                <Tabs defaultValue="procedures">
                                  <TabsList className="bg-gray-100 p-1 rounded-full w-fit">
                                    <TabsTrigger value="procedures" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Procedures</TabsTrigger>
                                    <TabsTrigger value="activities" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Activities</TabsTrigger>
                                  </TabsList>
                                  <TabsContent value="procedures" className="pt-4">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Procedure Name</TableHead>
                                          <TableHead>Department</TableHead>
                                          <TableHead>Owner</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {getSystemUsage(system.name).procedures.map((proc) => {
                                          const department = departments?.find(d => d.departmentCode === proc.departmentId)?.departmentName;
                                          const owner = users?.find(u => u.id === proc.procedureOwner)?.name;
                                          return (
                                            <TableRow key={proc.id}>
                                              <TableCell>{proc.procedureName}</TableCell>
                                              <TableCell>{department || 'Unknown'}</TableCell>
                                              <TableCell>{owner || 'Unknown'}</TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TabsContent>
                                  <TabsContent value="activities">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Activity Name</TableHead>
                                          <TableHead>Department</TableHead>
                                          <TableHead>Responsible People</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {getSystemUsage(system.name).activities.map((act) => {
                                          const procedure = procedures.find(p => p.id === act.procedure);
                                          const department = departments?.find(d => d.departmentCode === procedure?.departmentId)?.departmentName;
                                          const responsible = users?.find(u => u.id === act.responsiblePerson)?.name || 'Unknown';
                                          return (
                                            <TableRow key={act.id}>
                                              <TableCell>{act.activityName}</TableCell>
                                              <TableCell>{department || 'Unknown'}</TableCell>
                                              <TableCell>{responsible}</TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TabsContent>
                                </Tabs>
                              </div>
                              <DialogFooter>
                                <Button
                                  onClick={() => downloadUsageReport(system.name)}
                                  className="gap-2"
                                >
                                  <FileText size={16} />
                                  Download Report
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>

                          <Dialog
                            open={openEditSystem && editingSystem?.id === system.id}
                            onOpenChange={(open) => {
                              setOpenEditSystem(open);
                              if (open) setEditingSystem(system);
                              else setEditingSystem(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button variant="outline" size="icon">
                                <Edit size={16} />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit System</DialogTitle>
                                <DialogDescription>
                                  Update the system name and status.
                                </DialogDescription>
                              </DialogHeader>
                              {editingSystem && (
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="editSystemName">System Name</Label>
                                    <Input
                                      id="editSystemName"
                                      value={editingSystem.name}
                                      onChange={(e) =>
                                        setEditingSystem({
                                          ...editingSystem,
                                          name: e.target.value,
                                        })
                                      }
                                      placeholder="Enter system name"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>System Status</Label>
                                    <Select
                                      value={editingSystem.active ? "active" : "inactive"}
                                      onValueChange={(value) =>
                                        setEditingSystem({
                                          ...editingSystem,
                                          active: value === "active"
                                        })
                                      }
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="inactive">Inactive</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              )}
                              <DialogFooter>
                                <DialogClose asChild>
                                  <Button variant="outline">Cancel</Button>
                                </DialogClose>
                                <Button onClick={handleUpdateSystem}>Save Changes</Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>

                          <Dialog
                            open={openDeleteSystem && deleteSystemId === system.id}
                            onOpenChange={(open) => {
                              setOpenDeleteSystem(open);
                              if (open && system.id) setDeleteSystemId(system.id);
                              else setDeleteSystemId("");
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button variant="outline" size="icon" className="text-destructive">
                                <Trash2 size={16} />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Delete System</DialogTitle>
                                <DialogDescription>
                                  This action cannot be undone. The system will be removed from all
                                  activities where it is used.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                <p>
                                  Are you sure you want to delete the system "{system.name}"?
                                </p>
                              </div>
                              <DialogFooter>
                                <DialogClose asChild>
                                  <Button variant="outline">Cancel</Button>
                                </DialogClose>
                                <Button variant="destructive" onClick={handleDeleteSystem}>
                                  Delete
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
